import { z } from "zod";
import { JiraDraft, JiraFieldsRequired, JiraIssue, JiraIssueCreate } from "../schemas/jira";
import { request, validateInput } from "../request";
import type { ActionReturnType } from "../types";

type JiraIssueCreateResult =
  | Awaited<ActionReturnType<z.infer<typeof JiraIssue>>>
  | { ok: false; type: "fields_required"; error: string; status: 422; fieldsRequired: z.infer<typeof JiraFieldsRequired> };

export class JiraIssuesEndpoint {
  public static readonly basePath = "jira-issues";
  public static readonly schemas = {
    draft: {
      query: z.object({
        cycle_id: z.number().int().positive(),
        step_id: z.number().int().positive().optional(),
        project_key: z.string().trim().min(1).optional(),
        issue_type: z.string().trim().min(1).optional(),
      }),
      response: JiraDraft,
    },
    create: {
      body: JiraIssueCreate,
      response: JiraIssue,
      fieldsRequired: JiraFieldsRequired,
    },
    read: {
      params: z.object({ issue_id: z.number().int().positive() }),
      response: JiraIssue,
    },
  };

  public draft(
    query: z.input<typeof JiraIssuesEndpoint.schemas.draft.query>,
  ): ActionReturnType<z.infer<typeof JiraDraft>> {
    const valid = validateInput(JiraIssuesEndpoint.schemas.draft.query, query);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraIssues", "draft"],
      method: "get",
      path: [JiraIssuesEndpoint.basePath, "draft"],
      query: valid.data,
      localErrorHandling: true,
      response: JiraDraft,
    });
  }

  /** A 422 `jira_field_required` comes back as `fields_required` with the fields to render and resend. */
  public async create(body: z.input<typeof JiraIssuesEndpoint.schemas.create.body>): Promise<JiraIssueCreateResult> {
    const valid = validateInput(JiraIssuesEndpoint.schemas.create.body, body);
    if (!valid.ok) return valid;
    const result = await request({
      log: ["JiraIssues", "create"],
      method: "post",
      path: [JiraIssuesEndpoint.basePath],
      json: valid.data,
      localErrorHandling: true,
      response: JiraIssue,
    });
    if (result.ok || result.status !== 422) return result;
    const refusal = JiraFieldsRequired.safeParse(result.body);
    return refusal.success
      ? { ok: false, type: "fields_required", error: refusal.data.message, status: 422, fieldsRequired: refusal.data }
      : result;
  }

  public read(issueId: number): ActionReturnType<z.infer<typeof JiraIssue>> {
    const params = validateInput(JiraIssuesEndpoint.schemas.read.params, { issue_id: issueId });
    if (!params.ok) return Promise.resolve(params);
    return request({
      log: ["JiraIssues", "read"],
      method: "get",
      path: [JiraIssuesEndpoint.basePath, params.data.issue_id],
      localErrorHandling: true,
      response: JiraIssue,
    });
  }
}
